// ============================================================================
// COMPONENTE: ClientTasksCard.jsx — C3
// Exibe tarefas com prazo atribuídas pelo escritório ao cliente.
// Coleção: artifacts/{appId}/public/data/client_tasks
// Schema: { clientId, title, description, type, priority, dueDate,
//           status: pending|submitted|completed|rejected, rejectionNote, createdAt }
// ============================================================================

import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, FileText, FileSignature, ClipboardList, UserCheck } from 'lucide-react';
import { usePortalContext } from '../../context/PortalContext';
import { subscribeClientTasks, submitClientTask } from '../../services/ClientTasksRepository';

const TYPE_META = {
  document:  { label: 'Documento',   Icon: FileText,      color: 'text-blue-600',   bg: 'bg-blue-50'   },
  form:      { label: 'Formulário',   Icon: ClipboardList, color: 'text-purple-600', bg: 'bg-purple-50' },
  signature: { label: 'Assinatura',   Icon: FileSignature, color: 'text-amber-600',  bg: 'bg-amber-50'  },
  presence:  { label: 'Comparecer',   Icon: UserCheck,     color: 'text-green-600',  bg: 'bg-green-50'  },
};

const formatDue = (ts) => {
  if (!ts) return null;
  try {
    const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return null; }
};

const isOverdue = (ts) => {
  if (!ts) return false;
  try {
    const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
    return d < new Date();
  } catch { return false; }
};

export default function ClientTasksCard() {
  const { crmClient } = usePortalContext();
  const clientId = String(crmClient?.id || '').trim();

  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [notice, setNotice]   = useState('');

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    const unsub = subscribeClientTasks(clientId, (rows) => {
      setTasks(rows.filter(t => t.status === 'pending' || t.status === 'submitted' || t.status === 'rejected'));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [clientId]);

  const handleDone = async (task) => {
    if (working || (task.status !== 'pending' && task.status !== 'rejected')) return;
    setWorking(task.id);
    try {
      await submitClientTask(task.id);
      setNotice('Tarefa marcada como concluída!');
      setTimeout(() => setNotice(''), 3000);
    } catch { /* silent */ }
    setWorking(null);
  };

  if (loading || tasks.length === 0) return null;

  const pendingCount = tasks.filter(t => t.status === 'pending').length;

  return (
    <div id="tasks-section" className="border border-gray-300 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-[#20243B]" />
          <span className="text-xs font-bold text-[#20243B]">Tarefas do Escritório</span>
          {pendingCount > 0 && (
            <span className="h-5 min-w-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">
              {pendingCount}
            </span>
          )}
        </div>
      </div>

      <div className="px-3 py-3 space-y-2">
        {notice && (
          <div className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            {notice}
          </div>
        )}

        {tasks.map((task) => {
          const meta    = TYPE_META[task.type] || TYPE_META.document;
          const Icon    = meta.Icon;
          const urgent  = task.priority === 'urgent';
          const overdue = task.status === 'pending' && isOverdue(task.dueDate);
          const due     = formatDue(task.dueDate);
          const done     = task.status === 'submitted';
          const rejected = task.status === 'rejected';

          return (
            <div
              key={task.id}
              className={`rounded-lg border px-3 py-2.5 ${
                done
                  ? 'border-gray-200 bg-gray-50 opacity-60'
                  : rejected
                  ? 'border-red-200 bg-red-50'
                  : overdue
                  ? 'border-red-200 bg-red-50'
                  : urgent
                  ? 'border-amber-200 bg-amber-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-2.5">
                {/* Ícone tipo */}
                <div className={`mt-0.5 h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${meta.bg}`}>
                  <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-xs font-bold ${done ? 'text-gray-400 line-through' : 'text-[#20243B]'}`}>
                      {task.title}
                    </span>
                    {urgent && !done && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1 py-0.5 uppercase tracking-wide">
                        <AlertTriangle className="w-2.5 h-2.5" /> Urgente
                      </span>
                    )}
                    {overdue && !done && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-red-700 bg-red-100 border border-red-300 rounded px-1 py-0.5 uppercase tracking-wide">
                        Atrasado
                      </span>
                    )}
                  </div>

                  {task.description ? (
                    <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">{task.description}</p>
                  ) : null}
                  {rejected && task.rejectionNote && (
                    <div className="mt-1 flex items-start gap-1 text-[10px] text-red-700 bg-red-100 border border-red-200 rounded px-2 py-1">
                      <span className="font-bold shrink-0">Devolvida:</span>
                      <span>{task.rejectionNote}</span>
                    </div>
                  )}

                  {due && (
                    <div className={`flex items-center gap-1 mt-1 text-[10px] font-medium ${overdue && !done ? 'text-red-600' : 'text-gray-400'}`}>
                      <Clock className="w-3 h-3" />
                      Prazo: {due}
                    </div>
                  )}
                </div>

                {/* Ação */}
                {rejected ? (
                  <button
                    type="button"
                    onClick={() => handleDone(task)}
                    disabled={!!working}
                    className="shrink-0 text-[10px] font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-40"
                  >
                    {working === task.id ? '...' : 'Reenviar'}
                  </button>
                ) : done ? (
                  <div className="shrink-0 flex items-center gap-1 text-[10px] text-emerald-600 font-bold">
                    <CheckCircle2 className="w-4 h-4" />
                    Enviado
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleDone(task)}
                    disabled={!!working}
                    className="shrink-0 text-[10px] font-bold text-white bg-[#20243B] hover:bg-[#C5A059] hover:text-[#20243B] rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-40"
                  >
                    {working === task.id ? '...' : 'Concluído'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
